/**
 * WebSocket channel factory.
 * Creates a channel that emits WsMessage objects from the WebSocket.
 *
 * Compatible with both:
 * - redux-saga yield take(channel) — one subscriber at a time, replaced per call
 * - Direct channel.take(cb) — callback receives all messages
 */

import { WsMessage, WS_CLIENT_TYPES } from './types';

// Client-initiated heartbeat cadence. Intentionally aligned with iOS
// (WSClient.defaultPingInterval = 30s) so both surfaces present the
// same NAT-keepalive profile to proxies and the same dead-server
// detection latency.
//
// Different mechanism, same cadence: web sends an application-level
// {type:"ping"} JSON message because the browser WebSocket API doesn't
// expose protocol-level send; iOS sends a WS protocol-level ping via
// URLSessionWebSocketTask.ping(). Both are well within the server's
// 60s pongWait (gorilla ping cycle is 54s, independent of these
// client-initiated pings).
const HEARTBEAT_INTERVAL_MS = 30000;

export interface WebSocketChannelOptions {
  url: string;
  token: string;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface WebSocketChannel {
  take(cb: (message: WsMessage) => void): void;
  close(): void;
}

/**
 * Creates a WebSocket channel connected to the backend.
 * The channel emits parsed WsMessage objects.
 */
export function createWebSocketChannel(
  options: WebSocketChannelOptions
): WebSocketChannel {
  let currentSubscriber: ((msg: WsMessage) => void) | null = null;
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  const wsUrl = `${options.url}?token=${encodeURIComponent(options.token)}`;
  const ws = new WebSocket(wsUrl);

  function emit(msg: WsMessage) {
    if (currentSubscriber) {
      currentSubscriber(msg);
    }
  }

  ws.onopen = () => {
    heartbeatInterval = setInterval(() => {
      if (ws.readyState === 1) { // WebSocket.OPEN
        ws.send(JSON.stringify({ type: WS_CLIENT_TYPES.PING }));
      }
    }, HEARTBEAT_INTERVAL_MS);

    emit({
      type: 'WS_CONNECTED',
      data: null,
    });

    options.onOpen?.();
  };

  ws.onmessage = (event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as WsMessage;
      emit(message);
    } catch {
      // Ignore unparseable messages
    }
  };

  ws.onerror = () => {
    emit({
      type: 'WS_ERROR',
      data: { message: 'WebSocket error' },
    });
  };

  ws.onclose = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }

    emit({
      type: 'WS_DISCONNECTED',
      data: null,
    });

    options.onClose?.();
  };

  return {
    take(cb: (message: WsMessage) => void) {
      currentSubscriber = cb;
    },
    close() {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }
      ws.close();
    },
  };
}
